import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { AlertTriangle, ArrowLeft, Trash2 } from 'lucide-react'
import type {
  GetUserResponse,
  PortalAccessEvent,
  ReportCadence,
  ReportingMode,
  UpsertUserRequest,
  UserRole,
  UserStatusWrite,
} from '@natalan/capehost-contracts'
import { api } from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ErrorState } from '@/components/ui/error-state'
import { formatLocaleDateTime } from '@/lib/format-utils'
import { formatUserFullName, userStatusBadgeVariant } from '@/lib/user-utils'
import { UserReportPreviewCard } from '@/components/property/user-report-preview-card'
import { useToast } from '@/hooks/use-toast'

// Derived from api.getProperties' own return type (rather than the
// published PropertiesListResponse contract) so this stays in sync with
// client.ts's local `archived`/`managed_until` schema extension (CAP-171) --
// see that file's PropertyListItemWithSearchFieldsSchema doc comment for why
// those two fields aren't in the published contract yet.
type PropertyListItem = Awaited<ReturnType<typeof api.getProperties>>['properties'][number]

function toError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err))
}

/**
 * Dedicated user detail / edit page (replaces the previous modal flow).
 */
export function UserDetailsPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [loading, setLoading] = useState(true)
  const [pageError, setPageError] = useState<Error | null>(null)

  const [properties, setProperties] = useState<PropertyListItem[]>([])
  const [propertiesLoading, setPropertiesLoading] = useState(true)
  const [propertiesError, setPropertiesError] = useState<Error | null>(null)

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<Error | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const [previewConfirmOpen, setPreviewConfirmOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<Error | null>(null)
  const [previewFallbackUrl, setPreviewFallbackUrl] = useState<string | null>(null)

  const [formEmail, setFormEmail] = useState('')
  const [formFirstName, setFormFirstName] = useState('')
  const [formLastName, setFormLastName] = useState('')
  const [formRole, setFormRole] = useState<UserRole>('portal_user')
  const [formStatus, setFormStatus] = useState<UserStatusWrite>('invited')
  const [formInvitedAt, setFormInvitedAt] = useState<string | null>(null)
  const [formInviteSentAt, setFormInviteSentAt] = useState<string | null>(null)
  const [formLastLoginAt, setFormLastLoginAt] = useState<string | null>(null)
  const [formActivatedAt, setFormActivatedAt] = useState<string | null>(null)
  const [formCreatedAt, setFormCreatedAt] = useState<string | null>(null)
  const [formUpdatedAt, setFormUpdatedAt] = useState<string | null>(null)
  const [notifyNewReportsPublished, setNotifyNewReportsPublished] = useState(false)
  const [reportCadence, setReportCadence] = useState<ReportCadence>('weekly')
  const [reportDayOfWeek, setReportDayOfWeek] = useState(1)
  const [reportingMode, setReportingMode] = useState<ReportingMode>('gross')
  const [portalAccessEvents, setPortalAccessEvents] = useState<PortalAccessEvent[]>([])

  const [propertyQuery, setPropertyQuery] = useState('')
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([])
  // Backend-saved property assignments (distinct from the editable checkbox state
  // above) — the report preview uses these so it never previews an unsaved choice.
  const [savedPropertyIds, setSavedPropertyIds] = useState<string[]>([])

  const loadUser = useCallback(
    async (options: { silent?: boolean } = {}) => {
      const silent = Boolean(options.silent)
      if (!userId) return
      setPageError(null)
      if (!silent) setLoading(true)
      try {
        const data: GetUserResponse = await api.getUser(userId)
        const user = data.user
        setFormEmail(user.email || '')
        setFormFirstName(user.first_name || '')
        setFormLastName(user.last_name || '')
        setFormRole(user.role || 'portal_user')
        const st = user.status || 'invited'
        setFormStatus(st === ('inactive' as UserStatusWrite) ? 'disabled' : st)
        setFormInvitedAt(user.invited_at || null)
        setFormInviteSentAt(user.invite_sent_at || null)
        setFormLastLoginAt(user.last_portal_login_at || user.last_login_at || null)
        setFormActivatedAt(user.activated_at || null)
        setFormCreatedAt(user.created_at || null)
        setFormUpdatedAt(user.updated_at || null)
        setNotifyNewReportsPublished(Boolean(user.notify_new_reports_published))
        setReportCadence(user.report_cadence ?? 'weekly')
        setReportDayOfWeek(user.report_day_of_week ?? 1)
        setReportingMode(user.reporting_mode ?? 'gross')
        setPortalAccessEvents(
          Array.isArray(user.portal_access_events) ? user.portal_access_events : []
        )
        setSelectedPropertyIds(Array.isArray(user.property_ids) ? user.property_ids : [])
        setSavedPropertyIds(Array.isArray(user.property_ids) ? user.property_ids : [])
      } catch (err) {
        setPageError(toError(err))
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [userId]
  )

  useEffect(() => {
    const run = async () => {
      await loadUser({ silent: false })
    }
    run()
  }, [userId, loadUser])

  useEffect(() => {
    const loadProperties = async () => {
      try {
        // 'all' (not the default active-only) -- otherwise a property this
        // user is still assigned to, but that has since been archived, would
        // be silently absent from this list entirely instead of showing up
        // marked as archived (CAP-171).
        const data = await api.getProperties('all')
        setProperties(data.properties || [])
      } catch (err) {
        setPropertiesError(toError(err))
      } finally {
        setPropertiesLoading(false)
      }
    }
    loadProperties()
  }, [])

  const filteredProperties = useMemo(() => {
    const q = propertyQuery.trim().toLowerCase()
    const list = properties || []
    if (!q) return list
    return list.filter(p => {
      const name = String(p.display_name || p.name || '').toLowerCase()
      const id = String(p.id || p.property_id || '').toLowerCase()
      return name.includes(q) || id.includes(q)
    })
  }, [properties, propertyQuery])

  // The user's BACKEND-saved assigned properties as { id, name } for the report
  // preview selector. Built from savedPropertyIds (the assignment source of truth,
  // not the editable checkbox state) so it stays correct even before the full
  // properties list loads or if that request fails — names are filled in from
  // `properties` when available, otherwise the id is used as a fallback label.
  const userProperties = useMemo(() => {
    const byId = new Map(
      (properties || []).map(p => [String(p.id ?? p.property_id ?? ''), p] as const)
    )
    return savedPropertyIds
      .map(String)
      .filter(Boolean)
      .map(id => {
        const p = byId.get(id)
        return { id, name: (p && (p.display_name || p.name)) || id }
      })
  }, [properties, savedPropertyIds])

  const displayName = useMemo(() => {
    const n = formatUserFullName({ first_name: formFirstName, last_name: formLastName })
    return n === '—' ? formEmail || 'User' : n
  }, [formEmail, formFirstName, formLastName])

  const toggleProperty = (propertyId: string) => {
    setSelectedPropertyIds(prev => {
      const id = String(propertyId)
      if (prev.includes(id)) return prev.filter(x => x !== id)
      return [...prev, id]
    })
  }

  const saveUser = async () => {
    const email = String(formEmail || '')
      .trim()
      .toLowerCase()
    if (!email) {
      setSaveError(new Error('Email is required'))
      return
    }
    if (formRole === 'admin' && !email.endsWith('@capehost.ai')) {
      setSaveError(new Error('Admin users must use a @capehost.ai email address'))
      return
    }

    setSaving(true)
    setSaveError(null)
    try {
      // The contracts package types first_name/last_name as `string | undefined`
      // (no null), but the admin-api handler distinguishes "field omitted" from
      // "field explicitly cleared" and relies on receiving `null` here to clear a
      // name — see admin-users.js handleUpsertUser. Cast to preserve that behavior
      // exactly rather than silently switching to `undefined`, which would stop
      // clearing the name on save.
      const upsert = await api.upsertUser({
        email,
        first_name: String(formFirstName || '').trim() || null,
        last_name: String(formLastName || '').trim() || null,
        status: formStatus,
        role: formRole,
      } as UpsertUserRequest)

      const resolvedId = upsert?.user?.id || userId
      if (resolvedId) {
        await api.setUserProperties(resolvedId, selectedPropertyIds)
      }
      await loadUser({ silent: true })
      toast({
        title: 'Saved',
        description: 'User details and property access were updated.',
      })
    } catch (err) {
      setSaveError(toError(err))
    } finally {
      setSaving(false)
    }
  }

  const resendInvite = async () => {
    if (!userId) return
    if (formStatus !== 'invited') return
    setSaving(true)
    setSaveError(null)
    try {
      await api.resendUserInvite(userId)
      await loadUser({ silent: true })
      toast({
        title: 'Invite sent',
        description: 'Another password setup email was sent.',
      })
    } catch (err) {
      setSaveError(toError(err))
    } finally {
      setSaving(false)
    }
  }

  const confirmDeleteUser = async () => {
    if (!userId) return
    setSaving(true)
    setSaveError(null)
    try {
      await api.deleteUser(userId)
      setDeleteConfirmOpen(false)
      navigate('/users')
    } catch (err) {
      setSaveError(toError(err))
    } finally {
      setSaving(false)
    }
  }

  // Shared by the dialog's own onOpenChange AND the footer's Cancel/Close button:
  // Radix only fires onOpenChange for its own internal close triggers (Escape, overlay
  // click) — a custom footer button that calls `setPreviewConfirmOpen(false)` directly
  // bypasses it (Codex review), which left a blocked-tab session's fallback link stuck
  // on screen after Close, hiding "Start preview" on the next open.
  const handlePreviewDialogOpenChange = (open: boolean) => {
    setPreviewConfirmOpen(open)
    if (!open) {
      setPreviewError(null)
      setPreviewFallbackUrl(null)
    }
  }

  const confirmStartOwnerPreview = async () => {
    if (!userId) return
    setPreviewLoading(true)
    setPreviewError(null)
    setPreviewFallbackUrl(null)
    // Opened synchronously, still inside the click handler, before the `await` below --
    // a strict popup blocker only allows window.open() within the original user-gesture
    // window, which an await can fall outside of (Codex review). Deliberately NOT passing
    // `noopener` here: that makes window.open() return null even on success (also flagged
    // in review), which would make every successful preview misreport as blocked -- and
    // the tab we're opening is our own portal.capehost.ai, not an arbitrary external URL,
    // so the reverse-tabnabbing risk noopener guards against doesn't apply the way it
    // would for a third-party link.
    const tab = window.open('about:blank', '_blank')
    try {
      const res = await api.startPortalImpersonation(userId)
      if (tab) {
        tab.location.href = res.portalUrl
        setPreviewConfirmOpen(false)
        toast({
          title: 'Impersonation session started',
          description: `Viewing/acting as ${res.targetUser.email} for up to ${Math.round(res.ttlSeconds / 60)} minutes.`,
        })
      } else {
        // Genuinely blocked (or popups disabled entirely) -- the session still started
        // server-side, so keep the dialog open and hand back a real, user-clickable link
        // rather than discarding the only way to reach it.
        setPreviewFallbackUrl(res.portalUrl)
      }
    } catch (err) {
      tab?.close()
      setPreviewError(toError(err))
    } finally {
      setPreviewLoading(false)
    }
  }

  const applyStatus = async (nextStatus: UserStatusWrite) => {
    const email = String(formEmail || '')
      .trim()
      .toLowerCase()
    if (!email) {
      setSaveError(new Error('Email is required'))
      return
    }
    if (formRole === 'admin' && !email.endsWith('@capehost.ai')) {
      setSaveError(new Error('Admin users must use a @capehost.ai email address'))
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      await api.upsertUser({
        email,
        first_name: String(formFirstName || '').trim() || null,
        last_name: String(formLastName || '').trim() || null,
        role: formRole,
        status: nextStatus,
      } as UpsertUserRequest)
      setFormStatus(nextStatus)
      await loadUser({ silent: true })
      toast({
        title: 'Saved',
        description:
          nextStatus === 'disabled'
            ? 'This user is now disabled.'
            : 'This user was re-enabled and set to invited.',
      })
    } catch (err) {
      setSaveError(toError(err))
    } finally {
      setSaving(false)
    }
  }

  if (loading && !pageError) {
    return <div className="text-sm text-textMuted">Loading user…</div>
  }

  if (pageError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" className="-ml-2 gap-2" asChild>
          <Link to="/users">
            <ArrowLeft className="h-4 w-4" />
            Back to Users
          </Link>
        </Button>
        <ErrorState error={pageError} />
      </div>
    )
  }

  return (
    <div className="space-y-8 pb-8">
      <header className="space-y-4">
        <Button variant="ghost" size="sm" className="-ml-2 gap-2 w-fit" asChild>
          <Link to="/users">
            <ArrowLeft className="h-4 w-4" />
            Back to Users
          </Link>
        </Button>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight text-text truncate">
              {displayName}
            </h1>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-textMuted">
              <span className="truncate">{formEmail || '—'}</span>
              <Badge variant={userStatusBadgeVariant(formStatus)}>{formStatus}</Badge>
            </div>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px] xl:items-start">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>
                Contact and role. Status is shown in the side panel—you cannot set someone to active
                here; that happens when they complete signup and sign in.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="text-xs font-medium text-textMuted">Email</div>
                <Input
                  value={formEmail}
                  onChange={e => setFormEmail(e.target.value)}
                  placeholder="name@example.com"
                  autoComplete="email"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <div className="text-xs font-medium text-textMuted">First name</div>
                  <Input
                    value={formFirstName}
                    onChange={e => setFormFirstName(e.target.value)}
                    placeholder="First"
                    autoComplete="given-name"
                  />
                </div>
                <div className="space-y-2">
                  <div className="text-xs font-medium text-textMuted">Last name</div>
                  <Input
                    value={formLastName}
                    onChange={e => setFormLastName(e.target.value)}
                    placeholder="Last"
                    autoComplete="family-name"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button onClick={saveUser} disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Property access</CardTitle>
              <CardDescription>
                Choose which properties this user can access in the portal. Selected:{' '}
                {selectedPropertyIds.length}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input
                value={propertyQuery}
                onChange={e => setPropertyQuery(e.target.value)}
                placeholder="Search properties…"
              />
              {propertiesLoading ? (
                <div className="text-sm text-textMuted">Loading properties…</div>
              ) : propertiesError ? (
                <div className="rounded-lg border border-border bg-surface2 p-3 text-xs text-destructive">
                  Failed to load properties: {String(propertiesError?.message || propertiesError)}
                </div>
              ) : (
                <div className="max-h-[min(480px,50vh)] overflow-auto rounded-lg border border-border bg-bg p-2">
                  {filteredProperties.length === 0 ? (
                    <div className="p-3 text-sm text-textMuted">No matching properties.</div>
                  ) : (
                    <ul className="divide-y">
                      {filteredProperties.map(p => {
                        const id = String(p.id || p.property_id)
                        const name = p.display_name || p.name || id
                        const checked = selectedPropertyIds.includes(id)
                        // Properties now load with include=all so an archived
                        // property this user already has stays visible (see
                        // the comment on the fetch above) -- but that means
                        // archived properties this user does NOT have also
                        // show up here for the first time. An archived one
                        // can still be unchecked and rechecked freely if it
                        // was already part of the BACKEND-saved assignment
                        // (savedPropertyIds, not the mutable checkbox state)
                        // -- this page keeps the "unassign" ability it always
                        // had, including undoing an unsaved uncheck. Only a
                        // NEWLY checked archived property is blocked, since
                        // that would grant portal access to a property no
                        // one manages anymore. Basing this on live `checked`
                        // instead of the saved baseline made an unsaved
                        // uncheck irreversible (Codex PR review round 2).
                        const wasSavedAssigned = savedPropertyIds.includes(id)
                        const disableCheckbox = p.archived && !wasSavedAssigned
                        return (
                          <li key={id} className="flex items-center gap-3 p-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={disableCheckbox}
                              onChange={() => toggleProperty(id)}
                              title={
                                disableCheckbox
                                  ? 'Archived properties cannot be newly assigned'
                                  : undefined
                              }
                              className="h-4 w-4 accent-mint disabled:cursor-not-allowed disabled:opacity-50"
                            />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="truncate text-sm text-text">{name}</div>
                                {/* Read-only: this page only surfaces that access is
                                    stale, it never offers to unassign or deactivate
                                    from here -- that flow lives in the archive dialog's
                                    affected-users prompt (property-details.tsx). */}
                                {p.archived && (
                                  <Badge variant="secondary" className="shrink-0">
                                    Archived
                                  </Badge>
                                )}
                              </div>
                              <div className="truncate text-xs text-textMuted">{id}</div>
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              )}
              <Button variant="secondary" onClick={saveUser} disabled={saving}>
                {saving ? 'Saving…' : 'Save property access'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Account actions</CardTitle>
              <CardDescription>
                Invite email, disable, and re-enable. Re-send is only available while status is
                invited.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {formStatus === 'invited' ? (
                <div className="rounded-lg border border-border bg-surface2/80 p-4 space-y-3">
                  <p className="text-sm text-text">
                    Still waiting on signup? Send another password setup email (Auth0 limits may
                    apply).
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={saving}
                    onClick={resendInvite}
                  >
                    Re-send invite email
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-textMuted">
                  Re-send invite is only available while status is{' '}
                  <span className="text-text">invited</span>. For disabled accounts, use Re-enable
                  first if you need to invite again.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={saving || formStatus === 'disabled'}
                  onClick={() => applyStatus('disabled')}
                >
                  Disable user
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={saving || (formStatus !== 'disabled' && formStatus !== 'inactive')}
                  onClick={() => applyStatus('invited')}
                >
                  Re-enable (invited)
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Preview as owner</CardTitle>
              <CardDescription>
                Opens the owner portal in a new tab, viewing and acting as this owner — including
                creating or editing calendar blocks. Scoped to their assigned properties, logged,
                and expires automatically after 60 minutes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {formStatus !== 'active' ? (
                <p className="text-sm text-textMuted">
                  Only available once this user is active in the portal.
                </p>
              ) : savedPropertyIds.length === 0 ? (
                <p className="text-sm text-textMuted">
                  Assign at least one property before previewing as this owner.
                </p>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={saving}
                  onClick={() => {
                    setPreviewError(null)
                    setPreviewConfirmOpen(true)
                  }}
                >
                  Preview as owner…
                </Button>
              )}
            </CardContent>
          </Card>

          <UserReportPreviewCard
            userId={userId ?? ''}
            properties={userProperties}
            reportingMode={reportingMode}
          />
        </div>

        <aside className="space-y-4 xl:sticky xl:top-4 min-w-0">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Account overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <div className="text-xs font-medium text-textMuted">Status</div>
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant={userStatusBadgeVariant(formStatus)}>{formStatus}</Badge>
                </div>
                <p className="mt-1 text-xs text-textMuted">
                  Read-only. Becomes active after the first successful portal session with property
                  access, or disabled via actions.
                </p>
              </div>
              <div>
                <div className="text-xs font-medium text-textMuted">Report email notifications</div>
                <div className="mt-2 flex items-center gap-3">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={notifyNewReportsPublished}
                    aria-label="Report email notifications"
                    disabled={saving || !userId}
                    onClick={async () => {
                      if (!userId) return
                      const next = !notifyNewReportsPublished
                      setSaving(true)
                      setSaveError(null)
                      try {
                        await api.patchUser(userId, {
                          notify_new_reports_published: next,
                        })
                        setNotifyNewReportsPublished(next)
                        toast({
                          title: 'Saved',
                          description: `Report email notifications ${next ? 'enabled' : 'disabled'}.`,
                        })
                      } catch (err) {
                        setSaveError(toError(err))
                      } finally {
                        setSaving(false)
                      }
                    }}
                    className={[
                      'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors',
                      'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      notifyNewReportsPublished ? 'bg-mint' : 'bg-border',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-xs transition-transform',
                        notifyNewReportsPublished ? 'translate-x-4' : 'translate-x-0',
                      ].join(' ')}
                    />
                  </button>
                  <span className="text-sm text-text">
                    {notifyNewReportsPublished ? 'On' : 'Off'}
                  </span>
                </div>
                <p className="mt-1 text-xs text-textMuted">
                  When on, this user receives an email when new reports are published. The user can
                  also toggle this themselves in the portal.
                </p>
              </div>
              {notifyNewReportsPublished && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <div className="text-xs font-medium text-textMuted">Cadence</div>
                    <select
                      value={reportCadence}
                      disabled={saving || !userId}
                      onChange={async e => {
                        if (!userId) return
                        const next = e.target.value as ReportCadence
                        setSaving(true)
                        setSaveError(null)
                        try {
                          await api.patchUser(userId, { report_cadence: next })
                          await loadUser({ silent: true })
                          toast({ title: 'Saved', description: `Report cadence set to ${next}.` })
                        } catch (err) {
                          setSaveError(toError(err))
                        } finally {
                          setSaving(false)
                        }
                      }}
                      className="flex h-8 w-full rounded-input border border-border bg-surface2 px-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Biweekly</option>
                    </select>
                  </div>
                  {(reportCadence === 'weekly' || reportCadence === 'biweekly') && (
                    <div className="space-y-1">
                      <div className="text-xs font-medium text-textMuted">Day of week</div>
                      <select
                        value={reportDayOfWeek}
                        disabled={saving || !userId}
                        onChange={async e => {
                          if (!userId) return
                          const next = Number(e.target.value)
                          setSaving(true)
                          setSaveError(null)
                          try {
                            await api.patchUser(userId, { report_day_of_week: next })
                            await loadUser({ silent: true })
                            toast({
                              title: 'Saved',
                              description: `Report day set to ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][next]}.`,
                            })
                          } catch (err) {
                            setSaveError(toError(err))
                          } finally {
                            setSaving(false)
                          }
                        }}
                        className="flex h-8 w-full rounded-input border border-border bg-surface2 px-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {[
                          'Sunday',
                          'Monday',
                          'Tuesday',
                          'Wednesday',
                          'Thursday',
                          'Friday',
                          'Saturday',
                        ].map((day, i) => (
                          <option key={i} value={i}>
                            {day}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-1">
                <div className="text-xs font-medium text-textMuted">Revenue reporting</div>
                <select
                  value={reportingMode}
                  disabled={saving || !userId}
                  onChange={async e => {
                    if (!userId) return
                    const next = e.target.value as ReportingMode
                    setSaving(true)
                    setSaveError(null)
                    try {
                      // Update this field + backend-owned metadata (updated_at)
                      // from the patch response rather than reloading the whole
                      // record, which would discard unsaved edits to other fields.
                      const res = await api.patchUser(userId, { reporting_mode: next })
                      setReportingMode(res?.user?.reporting_mode ?? next)
                      if (res?.user?.updated_at) setFormUpdatedAt(res.user.updated_at)
                      toast({
                        title: 'Saved',
                        description: `Revenue reporting set to ${next === 'net' ? 'net (owner net)' : 'gross'}.`,
                      })
                    } catch (err) {
                      setSaveError(toError(err))
                    } finally {
                      setSaving(false)
                    }
                  }}
                  className="flex h-8 w-full rounded-input border border-border bg-surface2 px-2 text-sm text-text transition-all focus-visible:outline-hidden focus-visible:border-mint focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="gross">Gross revenue</option>
                  <option value="net">Net revenue (owner net)</option>
                </select>
                <p className="mt-1 text-xs text-textMuted">
                  Controls whether this owner&apos;s reports show gross rental revenue (default) or
                  Plan A owner-net. Net uses Uplisting per-booking actuals; verify a property&apos;s
                  commission is set before switching.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Key dates</CardTitle>
              <CardDescription>Invitation and sign-in timestamps from the API.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <MetadataRow label="Invited at" value={formatLocaleDateTime(formInvitedAt)} />
              <MetadataRow
                label="Last invite sent"
                value={formatLocaleDateTime(formInviteSentAt)}
              />
              <MetadataRow
                label="Last portal login"
                value={formatLocaleDateTime(formLastLoginAt)}
                hint="Updates on successful portal sessions (throttled for active users, at most about once per 15 minutes)."
              />
              <MetadataRow
                label="First activated at"
                value={formatLocaleDateTime(formActivatedAt)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Record</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <MetadataRow label="Created" value={formatLocaleDateTime(formCreatedAt)} />
              <MetadataRow label="Last updated" value={formatLocaleDateTime(formUpdatedAt)} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Portal activity</CardTitle>
              <CardDescription>
                Latest 10 events for support and debugging (portal sign-in and denials).
              </CardDescription>
            </CardHeader>
            <CardContent>
              {portalAccessEvents.length === 0 ? (
                <p className="text-sm text-textMuted">No portal events recorded yet.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {portalAccessEvents.map(ev => (
                    <li
                      key={ev.id || `${ev.occurred_at}-${ev.event_type}`}
                      className="border-b pb-2 last:border-0 last:pb-0"
                    >
                      <div className="text-text">{describePortalAccessEvent(ev)}</div>
                      <div className="text-xs text-textMuted">
                        {formatLocaleDateTime(ev.occurred_at)}
                        {ev.request_path ? ` · ${ev.request_path}` : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </aside>
      </div>

      <Card className="border-danger/30 bg-surface2/40">
        <CardHeader>
          <CardTitle className="text-danger">Danger zone</CardTitle>
          <CardDescription>
            Permanently remove this user from Capehost and drop their property access. This does not
            delete their Auth0 account by itself—only the local record here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="destructive"
            disabled={saving || !userId}
            onClick={() => {
              setSaveError(null)
              setDeleteConfirmOpen(true)
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete user…
          </Button>
        </CardContent>
      </Card>

      {saveError && !deleteConfirmOpen ? (
        <div className="rounded-lg border border-border bg-surface2 p-3 text-sm text-destructive">
          {String(saveError?.message || saveError)}
        </div>
      ) : null}

      <Dialog open={previewConfirmOpen} onOpenChange={handlePreviewDialogOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Preview as {displayName}?</DialogTitle>
            <DialogDescription>
              You&apos;ll be able to view and act as this owner in the portal — including creating
              or editing calendar blocks, which will be recorded as made by them, with a note that
              it was done on their behalf. This is logged and expires in 60 minutes.
            </DialogDescription>
          </DialogHeader>
          {previewError ? (
            <div className="rounded-lg border border-border bg-surface2 p-3 text-xs text-destructive">
              {String(previewError?.message || previewError)}
            </div>
          ) : null}
          {previewFallbackUrl ? (
            <div className="rounded-lg border border-border bg-surface2 p-3 text-sm text-text">
              The session started, but your browser blocked the new tab.{' '}
              <a
                href={previewFallbackUrl}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-mint underline"
              >
                Open the owner portal
              </a>
              .
            </div>
          ) : null}
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => handlePreviewDialogOpenChange(false)}
              disabled={previewLoading}
            >
              {previewFallbackUrl ? 'Close' : 'Cancel'}
            </Button>
            {!previewFallbackUrl && (
              <Button onClick={confirmStartOwnerPreview} disabled={previewLoading}>
                {previewLoading ? 'Starting…' : 'Start preview'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={open => {
          setDeleteConfirmOpen(open)
          if (!open) setSaveError(null)
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-danger">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              Delete user?
            </DialogTitle>
            <DialogDescription>
              This permanently removes the user record and property access in Capehost. This action
              cannot be undone from this screen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm text-textMuted">
            <p>
              <span className="font-medium text-text">Email: </span>
              {formEmail || '—'}
            </p>
          </div>
          {saveError ? (
            <div className="rounded-lg border border-border bg-surface2 p-3 text-xs text-destructive">
              {String(saveError?.message || saveError)}
            </div>
          ) : null}
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteUser} disabled={saving}>
              {saving ? 'Deleting…' : 'Delete user'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function MetadataRow({
  label,
  value,
  hint,
}: {
  label: string
  value: string | null
  hint?: string
}) {
  return (
    <div>
      <div className="text-xs font-medium text-textMuted">{label}</div>
      <div className="mt-0.5 text-text">{value}</div>
      {hint ? <p className="mt-1 text-xs text-textMuted">{hint}</p> : null}
    </div>
  )
}

function describePortalAccessEvent(event: PortalAccessEvent): string {
  const t = String(event.event_type || '')
  const r = event.reason != null ? String(event.reason) : ''
  const d = event.detail != null ? String(event.detail) : ''
  if (t === 'portal_login_success' && d === 'first_access') return 'First portal access'
  if (t === 'portal_login_success') return 'Successful portal sign-in'
  if (t === 'portal_first_access') return 'First portal access'
  if (t === 'portal_access_denied') {
    if (r === 'user_disabled') return 'Access denied: disabled'
    if (r === 'no_property_access') return 'Access denied: no property assignment'
    if (r === 'invite_not_sent') return 'Access denied: invite not sent'
    if (r === 'not_active') return 'Access denied: not active'
    return r ? `Access denied: ${r}` : 'Access denied'
  }
  return t || 'Portal event'
}
